import { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fmt } from '../lib/helpers';
import { invokeAuth0EdgeFunction } from '../lib/edgeFunctions';
import Badge from '../components/Badge';
import Loader from '../components/Loader';

const STATUS_BADGE = { new: 'pu', approved: 'bl', dismissed: '' };

export default function PlatformInvitations() {
  const { getAccessTokenSilently } = useAuth0();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('new');
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await invokeAuth0EdgeFunction('platform-console', { action: 'invitation_requests' }, getAccessTokenSilently);
      setRequests(Array.isArray(data?.requests) ? data.requests : []);
    } catch (e) {
      setError(e?.message || 'Failed to load invitation requests');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    load();
  }, [load]);

  function showToast(msg, isError = false) {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3500);
  }

  async function handleApprove(row) {
    setBusyId(row.id);
    try {
      const data = await invokeAuth0EdgeFunction(
        'platform-console',
        { action: 'approve_invitation_request', request_id: row.id },
        getAccessTokenSilently
      );
      showToast(data?.message || `Invite sent to ${row.email}.`);
      load();
    } catch (e) {
      showToast(e?.message || 'Approve failed', true);
    } finally {
      setBusyId(null);
    }
  }

  async function handleDismiss(row) {
    setBusyId(row.id);
    try {
      await invokeAuth0EdgeFunction(
        'platform-console',
        { action: 'dismiss_invitation_request', request_id: row.id },
        getAccessTokenSilently
      );
      showToast(`Dismissed request from ${row.email}.`);
      load();
    } catch (e) {
      showToast(e?.message || 'Dismiss failed', true);
    } finally {
      setBusyId(null);
    }
  }

  const counts = { new: 0, approved: 0, dismissed: 0 };
  requests.forEach((r) => { if (counts[r.status] !== undefined) counts[r.status] += 1; });
  const filtered = requests.filter((r) => (r.status || 'new') === statusFilter);

  return (
    <>
      <div className="topbar">
        <div>
          <div className="tbtitle">Invitation requests</div>
          <div className="tbcrumb">Leads from the marketing site&apos;s &quot;Request an invitation&quot; form</div>
        </div>
      </div>

      <div className="content">
        {error && <div className="form-error" style={{ margin: '0 0 16px' }}>{error}</div>}

        <div className="card">
          <div className="chdr">
            <span className="ctitle">Requests</span>
            <div className="filter-tabs" style={{ marginBottom: 0 }}>
              {[['new', 'New'], ['approved', 'Approved'], ['dismissed', 'Dismissed']].map(([key, label]) => (
                <button key={key} type="button" className={`ftab${statusFilter === key ? ' active' : ''}`}
                  onClick={() => setStatusFilter(key)}>
                  {label}
                  <span style={{ marginLeft: 4, opacity: 0.7 }}>{counts[key] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>

          {loading ? <Loader /> : filtered.length === 0 ? (
            <div className="empty">No {statusFilter} requests.</div>
          ) : (
            <div>
              <div className="ua-header-row">
                <span style={{ flex: 1.4 }}>Name</span>
                <span style={{ flex: 1.6 }}>Email</span>
                <span style={{ flex: 1.2 }}>Company</span>
                <span style={{ flex: 1.1 }}>Platform interest</span>
                <span style={{ flex: 0.8 }}>Requested</span>
                <span style={{ flex: 0.7 }}>Status</span>
                <span style={{ flex: '0 0 180px' }} />
              </div>
              {filtered.map((r) => (
                <div key={r.id} className="ua-row">
                  <div style={{ flex: 1.4, minWidth: 0 }}>
                    <div className="ua-name">{`${r.first_name || ''} ${r.last_name || ''}`.trim() || '—'}</div>
                  </div>
                  <div style={{ flex: 1.6, minWidth: 0 }}>
                    <div className="ua-email">{r.email}</div>
                  </div>
                  <div style={{ flex: 1.2, minWidth: 0 }}>
                    <span className="ua-meta">{r.company || '—'}</span>
                  </div>
                  <div style={{ flex: 1.1, minWidth: 0 }}>
                    <span className="ua-meta">{r.platform_interest || '—'}</span>
                  </div>
                  <div style={{ flex: 0.8 }}>
                    <span className="ua-meta">{fmt(r.created_at)}</span>
                  </div>
                  <div style={{ flex: 0.7 }}>
                    <Badge variant={STATUS_BADGE[r.status] || ''}>{r.status || 'new'}</Badge>
                  </div>
                  <div style={{ flex: '0 0 180px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    {(r.status || 'new') === 'new' && (
                      <>
                        <button
                          type="button"
                          className="row-act"
                          disabled={busyId === r.id}
                          onClick={() => handleDismiss(r)}
                        >
                          Dismiss
                        </button>
                        <button
                          type="button"
                          className="btn-primary"
                          style={{ padding: '4px 12px', fontSize: 12 }}
                          disabled={busyId === r.id}
                          onClick={() => handleApprove(r)}
                        >
                          {busyId === r.id ? 'Approving…' : 'Approve & invite'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div className={toast.isError ? 'toast-error' : 'toast-success'}>
          {toast.msg}
        </div>
      )}
    </>
  );
}
